import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { useTranslationContext } from '@/contexts/TranslationContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Search, Save, Plus, Languages, X, Check, ArrowRight } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { getLocaleInfo } from '@/utils/localeUtils';
import { Skeleton } from '@/components/ui/skeleton';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { isTranslationAvailable, getEffectiveTranslationValue } from '@/utils/translationUtils';

interface TranslationData {
  id: string;
  translation_key: string;
  value: string;
  locale: string;
}

interface TranslationDefault {
  id: string;
  key: string;
  default_value: string;
  description: string | null;
}

interface TranslationGroup {
  name: string;
  keys: TranslationKey[];
}

interface TranslationKey {
  key: string;
  defaultValue: string;
  description: string | null;
  translations: Record<string, string>;
}

const TranslationsManagement = () => {
  const { isAllowedTo } = useCommunityMember();
  const { currentCommunity } = useCommunity();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { availableLocales, locale: currentLocale } = useTranslationContext();

  const [selectedLocale, setSelectedLocale] = useState<string>(currentLocale);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [modifiedTranslations, setModifiedTranslations] = useState<Record<string, string>>({});

  useEffect(() => {
    const checkAccess = async () => {
      const canManageTranslations = await isAllowedTo('manage_translations');
      if (!canManageTranslations) {
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "You don't have permission to manage translations."
        });
        navigate('/admin');
      }
    };

    checkAccess();
  }, [isAllowedTo, navigate, toast]);

  const { data: translationDefaults, isLoading: isLoadingDefaults } = useQuery({
    queryKey: ['translation-defaults'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('translation_defaults')
        .select('*')
        .order('key');
        
      if (error) throw error;
      return data as TranslationDefault[];
    },
  });

  const { data: communityTranslations, isLoading: isLoadingTranslations } = useQuery({
    queryKey: ['community-translations', currentCommunity?.id, selectedLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('community_translations')
        .select('*')
        .eq('community_id', currentCommunity.id)
        .eq('locale', selectedLocale);
        
      if (error) throw error;
      return data as TranslationData[];
    },
    enabled: !!currentCommunity?.id
  });

  const saveTranslationMutation = useMutation({
    mutationFn: async ({ translationKey, value }: { translationKey: string, value: string }) => {
      const { data: existingTranslation } = await supabase
        .from('community_translations')
        .select('id')
        .eq('community_id', currentCommunity.id)
        .eq('locale', selectedLocale)
        .eq('translation_key', translationKey)
        .maybeSingle();

      if (existingTranslation) {
        const { error } = await supabase
          .from('community_translations')
          .update({ value })
          .eq('id', existingTranslation.id);
          
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('community_translations')
          .insert({
            community_id: currentCommunity.id,
            locale: selectedLocale,
            translation_key: translationKey,
            value
          });
          
        if (error) throw error;
      }

      return { translationKey, value };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-translations'] });
    }
  });

  const saveAllTranslations = async () => {
    const keys = Object.keys(modifiedTranslations);
    
    if (keys.length === 0) {
      toast({
        description: "No changes to save"
      });
      return;
    }
    
    try {
      const savePromises = keys.map(key => 
        saveTranslationMutation.mutateAsync({
          translationKey: key,
          value: modifiedTranslations[key]
        })
      );
      
      await Promise.all(savePromises);
      
      toast({
        title: "Translations saved",
        description: `Successfully saved ${keys.length} translations`
      });
      
      setModifiedTranslations({});
    } catch (error) {
      console.error('Error saving translations:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save translations"
      });
    }
  };

  const handleTranslationChange = (key: string, value: string) => {
    setModifiedTranslations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const processTranslations = () => {
    if (!translationDefaults) return [];

    const translationsMap: Record<string, string> = {};
    communityTranslations?.forEach(translation => {
      translationsMap[translation.translation_key] = translation.value;
    });

    const groupsMap: Record<string, TranslationKey[]> = {};
    
    translationDefaults.forEach(defaultTranslation => {
      const key = defaultTranslation.key;
      const groupName = key.split('.')[0];
      
      if (!groupsMap[groupName]) {
        groupsMap[groupName] = [];
      }
      
      groupsMap[groupName].push({
        key,
        defaultValue: defaultTranslation.default_value,
        description: defaultTranslation.description,
        translations: {
          [selectedLocale]: translationsMap[key] || ''
        }
      });
    });

    return Object.keys(groupsMap).sort().map(groupName => ({
      name: groupName,
      keys: groupsMap[groupName]
    }));
  };

  const filterTranslations = (groups: TranslationGroup[]) => {
    if (!searchQuery) return groups;
    
    return groups.map(group => ({
      ...group,
      keys: group.keys.filter(item => 
        item.key.toLowerCase().includes(searchQuery.toLowerCase()) || 
        item.defaultValue.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.translations[selectedLocale] && 
          item.translations[selectedLocale].toLowerCase().includes(searchQuery.toLowerCase()))
      )
    })).filter(group => group.keys.length > 0);
  };

  const translationGroups = filterTranslations(processTranslations());
  const isLoading = isLoadingDefaults || isLoadingTranslations;
  const hasChanges = Object.keys(modifiedTranslations).length > 0;

  const groupNames = translationGroups.map(group => group.name);

  useEffect(() => {
    if (groupNames.length > 0 && !selectedGroup) {
      setSelectedGroup(groupNames[0]);
    }
  }, [groupNames, selectedGroup]);

  const getMissingTranslationsCount = (keys: TranslationKey[]) => {
    return keys.reduce((count, item) => {
      const hasTranslation = isTranslationAvailable(
        item.translations[selectedLocale],
        item.defaultValue,
        selectedLocale
      );
      return count + (hasTranslation ? 0 : 1);
    }, 0);
  };

  const getKeyDisplay = (key: string) => {
    const parts = key.split('.');
    // Show last 2 parts for better context, or just the last part if there's only one
    const displayParts = parts.length > 1 ? parts.slice(-2) : parts;
    const displayKey = displayParts.join('.');
    
    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <span className="font-mono text-xs cursor-help">
            {displayKey}
          </span>
        </HoverCardTrigger>
        <HoverCardContent className="w-auto p-2">
          <p className="font-mono text-xs">{key}</p>
        </HoverCardContent>
      </HoverCard>
    );
  };

  const getLocaleDisplay = (localeCode: string) => {
    const localeInfo = getLocaleInfo(localeCode);
    return (
      <div className="flex items-center gap-2">
        <span className="text-lg">{localeInfo.flag}</span>
        <span>{localeInfo.displayName}</span>
      </div>
    );
  };

  const showLanguageSelector = availableLocales.length > 1;

  const renderDescription = (description: string | null, fullKey: string) => {
    if (!description) return null;
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" className="inline-flex items-center ml-2 text-muted-foreground hover:text-foreground">
              <Info className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-sm font-mono">{fullKey}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderSkeletons = () => (
    <div className="space-y-6">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-4 w-1/4" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div className="relative">
        <h1 className="admin-section-title mb-2">Translation Management</h1>
        <p className="text-muted-foreground/80 text-lg">
          Manage translations for your community
        </p>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Translation Editor</h2>
          <p className="text-sm text-muted-foreground/80">Edit translations for {currentCommunity.name}</p>
        </div>
        
        {hasChanges && (
          <Button 
            onClick={saveAllTranslations} 
            className="gap-2 rounded-lg  px-6 py-2.5 w-full sm:w-auto"
            disabled={saveTranslationMutation.isPending}
          >
            {saveTranslationMutation.isPending ? 'Saving...' : 'Save All Changes'}
            <Save className="h-4 w-4" />
          </Button>
        )}
      </div>

      <Card className="admin-card admin-card-stats">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Languages className="h-5 w-5 text-blue-600" />
            <span>Translation Editor</span>
          </CardTitle>
          <CardDescription className="text-muted-foreground/80">
            Customize translations for your community in different languages
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col gap-4 sm:gap-6">
              {showLanguageSelector && (
                <div className="space-y-3">
                  <Label htmlFor="locale-select" className="text-sm font-medium text-slate-700">Select Language</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {availableLocales.map(locale => (
                      <Button
                        key={locale}
                        type="button"
                        variant={locale === selectedLocale ? "default" : "outline"}
                        onClick={() => setSelectedLocale(locale)}
                        className="justify-start h-12 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                      >
                        {getLocaleDisplay(locale)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-3">
                <Label htmlFor="search" className="text-sm font-medium text-slate-700">Search Translations</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    type="search"
                    placeholder="Search keys or text..."
                    className="pl-10 h-12 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {hasChanges && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTitle className="text-amber-600 flex items-center gap-2">
                  <span>Unsaved Changes</span>
                  <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-700 hover:bg-amber-100">
                    {Object.keys(modifiedTranslations).length} modified
                  </Badge>
                </AlertTitle>
                <AlertDescription className="text-amber-700">
                  You have unsaved translation changes. Click "Save All Changes" to persist them.
                </AlertDescription>
              </Alert>
            )}

            {isLoading ? (
              renderSkeletons()
            ) : translationGroups.length === 0 ? (
              <Alert>
                <AlertTitle>No translations found</AlertTitle>
                <AlertDescription>
                  {searchQuery 
                    ? "No translations match your search query. Try a different search term." 
                    : "No translation keys are available."}
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {isMobile ? (
                  <div className="space-y-4 lg:hidden">
                    {translationGroups.map((group, groupIndex) => (
                      <Card key={group.name} className={`admin-card admin-card-stats animate-fade-in stagger-${Math.min(groupIndex + 1, 5)} `}>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base font-semibold text-slate-800 flex items-center justify-between">
                            <span className="capitalize">{group.name}</span>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {group.keys.length}
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <Accordion type="single" collapsible className="w-full">
                            {group.keys.map((item, index) => (
                              <AccordionItem key={item.key} value={item.key} className="border-gray-100">
                                <AccordionTrigger className="hover:no-underline py-3">
                                  <div className="flex flex-col items-start gap-1">
                                    {getKeyDisplay(item.key)}
                                    {modifiedTranslations[item.key] && (
                                      <Badge variant="outline" className="bg-amber-100 text-amber-700 text-xs">
                                        Modified
                                      </Badge>
                                    )}
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="pt-2">
                                  <div className="space-y-4">
                                    <div className="space-y-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{getLocaleInfo('en').flag}</span>
                                        <Label className="font-medium text-sm">Reference</Label>
                                      </div>
                                      <div className="p-3 bg-gray-50 rounded-lg text-sm border border-gray-100">
                                        {item.defaultValue}
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor={`translation-${item.key}`} className="flex items-center gap-2">
                                        {getLocaleDisplay(selectedLocale)}
                                      </Label>
                                      {item.defaultValue.length > 100 ? (
                                        <Textarea
                                          id={`translation-${item.key}`}
                                          placeholder="Enter translation..."
                                          value={modifiedTranslations[item.key] ?? getEffectiveTranslationValue(item.translations[selectedLocale], item.defaultValue, selectedLocale)}
                                          onChange={(e) => handleTranslationChange(item.key, e.target.value)}
                                          className={`transition-all duration-200 ${modifiedTranslations[item.key] ? "border-amber-400 bg-amber-50/30" : ""}`}
                                          rows={4}
                                        />
                                      ) : (
                                        <Input
                                          id={`translation-${item.key}`}
                                          placeholder="Enter translation..."
                                          value={modifiedTranslations[item.key] ?? getEffectiveTranslationValue(item.translations[selectedLocale], item.defaultValue, selectedLocale)}
                                          onChange={(e) => handleTranslationChange(item.key, e.target.value)}
                                          className={`transition-all duration-200 h-12 ${modifiedTranslations[item.key] ? "border-amber-400 bg-amber-50/30" : ""}`}
                                        />
                                      )}
                                      {item.description && (
                                        <p className="text-xs text-muted-foreground/80 mt-2 px-1">
                                          {item.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Tabs 
                    value={selectedGroup || groupNames[0]} 
                    onValueChange={setSelectedGroup}
                    className="hidden lg:block"
                  >
                    <div className="relative mb-6">
                      <TabsList className="w-full justify-start gap-2 h-auto p-2 bg-gray-50/80 rounded-xl">
                        {groupNames.map(name => {
                          const group = translationGroups.find(g => g.name === name);
                          const missingCount = getMissingTranslationsCount(group?.keys || []);
                          
                          return (
                            <TabsTrigger 
                              key={name} 
                              value={name} 
                              className="relative capitalize px-4 py-3 h-auto rounded-lg transition-all duration-200 hover:bg-white/60 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                            >
                              <span className="font-medium">{name}</span>
                              <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200">
                                <span className="relative inline-block">
                                  {group?.keys.length || 0}
                                  {missingCount > 0 && (
                                    <span className="absolute top-0 right-0 translate-x-[90%] translate-y-[-55%] w-2.5 h-2.5 bg-destructive rounded-full border-2 border-background" />
                                  )}
                                </span>
                              </Badge>
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>
                    </div>
                    
                    {groupNames.map(groupName => (
                      <TabsContent key={groupName} value={groupName} className="mt-0">
                        <div className="grid gap-6">
                          {translationGroups
                            .find(g => g.name === groupName)
                            ?.keys.map((item, index) => (
                              <Card key={item.key} className={`admin-card admin-card-stats animate-fade-in stagger-${Math.min(index + 1, 5)}`}>
                                <CardContent className="p-6">
                                  <div className="grid lg:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                      <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-2">
                                          <span className="text-lg">{getLocaleInfo('en').flag}</span>
                                          <Label className="font-medium text-slate-700">Reference</Label>
                                        </div>
                                        {renderDescription(item.description, item.key)}
                                      </div>
                                      <div className="p-4 bg-gray-50 rounded-lg text-sm border border-gray-100">
                                        {item.defaultValue}
                                      </div>
                                      {item.description && (
                                        <p className="text-xs text-muted-foreground/80 px-1">
                                          {item.description}
                                        </p>
                                      )}
                                    </div>
                                    
                                    <div className="space-y-3">
                                      <Label htmlFor={`translation-${item.key}`} className="flex items-center gap-2">
                                        {getLocaleDisplay(selectedLocale)}
                                        {modifiedTranslations[item.key] && (
                                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                                            Modified
                                          </Badge>
                                        )}
                                      </Label>
                                      {item.defaultValue.length > 100 ? (
                                        <Textarea
                                          id={`translation-${item.key}`}
                                          placeholder="Enter translation..."
                                          value={modifiedTranslations[item.key] ?? getEffectiveTranslationValue(item.translations[selectedLocale], item.defaultValue, selectedLocale)}
                                          onChange={(e) => handleTranslationChange(item.key, e.target.value)}
                                          className={`transition-all duration-200 ${modifiedTranslations[item.key] ? "border-amber-400 bg-amber-50/30" : ""}`}
                                          rows={Math.min(6, Math.max(4, Math.ceil(item.defaultValue.length / 100)))}
                                        />
                                      ) : (
                                        <Input
                                          id={`translation-${item.key}`}
                                          placeholder="Enter translation..."
                                          value={modifiedTranslations[item.key] ?? getEffectiveTranslationValue(item.translations[selectedLocale], item.defaultValue, selectedLocale)}
                                          onChange={(e) => handleTranslationChange(item.key, e.target.value)}
                                          className={`transition-all duration-200 h-12 ${modifiedTranslations[item.key] ? "border-amber-400 bg-amber-50/30" : ""}`}
                                        />
                                      )}
                                      <div className="flex justify-end gap-2">
                                        {modifiedTranslations[item.key] && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              const { [item.key]: _, ...rest } = modifiedTranslations;
                                              setModifiedTranslations(rest);
                                            }}
                                            className="h-8 px-3 text-muted-foreground"
                                          >
                                            Reset
                                            <X className="ml-1 h-3 w-3" />
                                          </Button>
                                        )}
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            const value = modifiedTranslations[item.key] ?? getEffectiveTranslationValue(item.translations[selectedLocale], item.defaultValue, selectedLocale);
                                            saveTranslationMutation.mutate({ translationKey: item.key, value });
                                            const { [item.key]: _, ...rest } = modifiedTranslations;
                                            setModifiedTranslations(rest);
                                          }}
                                          className="h-8 px-3 text-primary border-primary/20 hover:bg-primary/5"
                                          disabled={!modifiedTranslations[item.key]}
                                        >
                                          Save
                                          <Check className="ml-1 h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                )}
              </>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col sm:flex-row justify-between border-t p-6 gap-4">
          <div className="text-sm text-muted-foreground/80">
            {communityTranslations?.length || 0} translations configured for {selectedLocale}
          </div>
          {hasChanges && (
            <Button 
              onClick={saveAllTranslations}
              disabled={saveTranslationMutation.isPending}
              className="gap-2 rounded-lg  px-6 py-2.5 w-full sm:w-auto"
            >
              {saveTranslationMutation.isPending ? 'Saving...' : 'Save All Changes'}
              <Save className="h-4 w-4" />
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};

export default TranslationsManagement;
